// src/config/theme.default.ts
import { BrandConfig } from "../types/brand";

// Default brand configuration (ACTUAL 8020agent colors)
const defaultBrandConfig: BrandConfig = {
  // App Identity
  appName: "8020agent",
  tagline: "Google Ads Optimization Made Easy",
  domain: "8020agent.com",
  version: "1.5.0", // Will sync with package.json

  // Promotional Banner Configuration
  promoBanner: {
    enabled: true,
    title: "See what AI can do. Check out 8020brain.com for a range of demos, including search term classification and a Google Ads audit.",
    subtitle: "",
    linkUrl: "https://8020brain.com/google-ads",
    dismissible: false,
  },

  // Community Member Banner (shown to active Ads to AI members instead of promoBanner)
  // Disabled 2026-06-12: known members see NO banner. The copy was also out of
  // date (some members already own the brain, e.g. Agency Brain buyers). Kept
  // here, dismissible, so any future member-facing banner can be re-enabled and
  // dismissed by the member.
  communityBanner: {
    enabled: false,
    title: "Have you tried your free 8020brain yet?",
    subtitle:
      "It's included in your Ads To AI membership. try it now!",
    linkUrl: "https://mikerhodes.circle.so/c/8020brain/",
    dismissible: true,
  },

  // Visual Branding
  logo: {
    url: "/logo-light.png",
    altText: "8020agent Logo",
    width: 30,
    height: 30,
  },

  nav: {
    showTitle: true,
  },

  // Typography Configuration
  typography: {
    // Font families for different text types
    fontFamily: {
      sans: "Inter", // Body and UI font - default Inter
      heading: "Inter", // Heading font - default Inter
      mono: "JetBrains Mono", // Monospace font for code
    },
    // Font weights
    fontWeight: {
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
    },
  },

  // Color Scheme
  themes: {
    light: {
      primary: "hsl(20, 84%, 45%)",
      primaryHover: "hsl(22, 70%, 50%)",
      primaryForeground: "hsl(210, 20%, 98%)",
      secondary: "hsl(243, 75%, 50%)",
      secondaryHover: "hsl(243, 75%, 55%)",
      secondaryForeground: "hsl(210, 20%, 98%)",
      accent: "hsl(220, 14.3%, 95.9%)",
      accentForeground: "hsl(220.9, 39.3%, 11%)",
      success: "hsl(160, 60%, 55%)",
      warning: "hsl(43, 74%, 66%)",
      danger: "hsl(0, 84.2%, 60.2%)",
      background: "hsl(0 0% 100%)",
      foreground: "hsl(224 71.4% 4.1%)",
      card: "hsl(0 0% 100%)",
      cardForeground: "hsl(224 71.4% 4.1%)",
      popover: "hsl(0 0% 100%)",
      popoverForeground: "hsl(224 71.4% 4.1%)",
      muted: "hsl(220, 14.3%, 95.9%)",
      mutedForeground: "hsl(220, 8.9%, 46.1%)",
      border: "hsl(220, 13%, 91%)",
      input: "hsl(220, 13%, 91%)",
      ring: "hsl(20, 84%, 45%)",
      chartPalette: [
        "hsl(12, 76%, 61%)",
        "hsl(243, 75%, 59%)",
        "hsl(173, 58%, 39%)",
        "hsl(43, 74%, 66%)",
        "hsl(27, 87%, 67%)",
      ],
    },
    dark: {
      primary: "hsl(20, 84%, 50%)", // Slightly brighter for dark mode
      primaryHover: "hsl(22, 70%, 55%)",
      primaryForeground: "hsl(210, 20%, 98%)",
      secondary: "hsl(243, 75%, 65%)", // Lighter indigo for dark mode
      secondaryHover: "hsl(243, 75%, 70%)",
      secondaryForeground: "hsl(210, 20%, 98%)",
      accent: "hsl(220, 15%, 20%)",
      accentForeground: "hsl(210, 20%, 98%)",
      success: "hsl(160, 60%, 55%)",
      warning: "hsl(43, 74%, 66%)",
      danger: "hsl(0, 72%, 51%)",
      background: "hsl(224, 71%, 4%)", // Dark background
      foreground: "hsl(210, 20%, 98%)",
      card: "hsl(224, 71%, 6%)",
      cardForeground: "hsl(210, 20%, 98%)",
      popover: "hsl(224, 71%, 4%)",
      popoverForeground: "hsl(210, 20%, 98%)",
      muted: "hsl(220, 15%, 20%)",
      mutedForeground: "hsl(220, 9%, 65%)",
      border: "hsl(220, 15%, 20%)",
      input: "hsl(220, 15%, 20%)",
      ring: "hsl(20, 84%, 50%)",
      chartPalette: [
        "hsl(12, 76%, 65%)",
        "hsl(243, 75%, 65%)",
        "hsl(173, 58%, 45%)",
        "hsl(43, 74%, 70%)",
        "hsl(27, 87%, 70%)",
      ], // Brighter palette for dark mode
    },
  },

  // Company Information
  company: {
    name: "8020agent",
    legalName: "Off Rhodes Pty Ltd",
    website: "https://8020agent.com",
    supportEmail: "support@mikerhodes.com.au",
    privacyEmail: "support@mikerhodes.com.au",
    address: {
      street: "24 Holroyd Street",
      city: "Kew, VIC",
      country: "Australia",
      postal: "3101",
    },
    phone: "+61 0414 932727",
  },

  // Legal Information
  legal: {
    copyrightYear: new Date().getFullYear(),
    privacyPolicyLastUpdated: "2025-01-01",
    termsOfServiceLastUpdated: "2025-01-01",
  },

  // Support Configuration
  support: {
    helpUrl: "https://mikerhodes.circle.so/c/help/",
    helpText: "Support",
    showFeedback: true,
  },

  // Navigation Control
  navigation: {
    // Main menu items that can be hidden for white-label
    hideItems: {
      Buy: false, // Hide buy page
      "Build the Agent": false, // Hide build page
      Membership: false, // Hide membership page
      "4Cs": false, // Hide 4Cs page
    },

    // Account dropdown items (currently just settings + user button)
    accountDropdown: {
      showSettings: true,
    },
  },

  // Feature Toggles
  features: {
    // Core analysis features (always enabled)
    dataAnalysis: true,
    insights: true,
    learningContent: true,

    // Optional features that can be toggled
    promotionalBanner: true,
    feedbackWidget: true,
    salesPages: true,
    showMembership: true, // ONLY true for original 8020agent

    // Educational content
    scaryScripts: true,
    nleScripts: true,
    psaScripts: true,
  },

  // Page Content Customization
  pages: {
    home: {
      welcomeMessage: "Welcome to your Google Ads optimization dashboard",
      showGettingStarted: true,
      showRecentAnalysis: true,
      // Call-to-action customization
      signedInCTA: "Ready to optimize your campaigns?",
      signedOutCTA: "Get Started with 8020agent",
      dataButtonText: "See My Data",
    },
  },
};

export default defaultBrandConfig;
